import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import { useAppColors } from '../../hooks/useAppColors';
import { useGoogleSignIn } from '../../hooks/useAuth';
import { getApiError } from '../../utils/formatters';

interface Props {
  /** Optional callback after Google sign-in succeeds. Receives the sync response (undefined on web redirect). */
  onSuccess?: (response: any) => void;
}

/**
 * "Continue with Google" button + error surface.
 * Encapsulates the Google SSO mutation and default post-login navigation
 * (routes to AuthCallback for profile completion when needed).
 *
 * The "or" divider is rendered separately (see AuthDivider) so that the
 * Apple sign-in button can sit between the Google button and the divider.
 */
export default function GoogleSignInButton({ onSuccess }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const navigation = useNavigation<any>();
  const googleSignIn = useGoogleSignIn();

  const handlePress = () => {
    googleSignIn.mutate(undefined, {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
          return;
        }
        if (!response) return; // web redirect, no response
        const needsCompletion =
          response.is_new_user ||
          !response.user.dob ||
          !response.user.terms_accepted_at;
        if (needsCompletion) {
          navigation.navigate('AuthCallback', { syncResult: response });
        }
      },
    });
  };

  return (
    <>
      <TouchableOpacity
        onPress={handlePress}
        disabled={googleSignIn.isPending}
        style={[styles.socialButton, { backgroundColor: colors.card, borderColor: colors.border }]}
        activeOpacity={0.8}
      >
        {googleSignIn.isPending ? (
          <ActivityIndicator size="small" color={colors.text} />
        ) : (
          <>
            <Icon name="google" size={20} color={colors.text} style={styles.socialIcon} />
            <Text style={[styles.socialButtonText, { color: colors.text }]}>
              {t('auth.continueWithGoogle')}
            </Text>
          </>
        )}
      </TouchableOpacity>

      {googleSignIn.isError && (
        <Text style={[styles.error, { color: colors.error }]}>
          {getApiError(googleSignIn.error, t('auth.loginFailed'), t)}
        </Text>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  socialButton: {
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  socialIcon: {
    marginRight: 8,
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  error: {
    textAlign: 'center',
    marginTop: 12,
    fontSize: 13,
  },
});
